import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import useClickOutside from '../../../hooks/useClickOutside';
import { ModalPagarSuscripcion } from './ModalPagarSuscripcion';
import { openModalBottom } from '../../../actions/ui';
import {
  startLoadSuscripcionCompleta,
  startLoadProfesionalesActivos,
  startLoadTarjeta,
  startLoadSuscripcionActiva
} from '../../../actions/suscripciones';
import { startLoadingTransacciones } from '../../../actions/transacciones';
import EditarFechaFinal from './EditarFechaFinal';

const InformacionPago = ({ suscripcion, onUpdate }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { colores } = useSelector(state => state.ui);
  const { empresaSuscripcion, profesionalesActivos, tarjeta } = useSelector(state => state.suscripciones);
  const { role } = useSelector(state => state.auth);
  const isSuperAdmin = role === 'SUPERADMIN_ROLE' || role === 'ADMIN_ROLE';

  const [modalTarjetaAbierta, setModalTarjetaAbierta] = useState(false);
  const [accionModal, setAccionModal] = useState('');

  const modalRef = useRef(null);
  const cerrarModal = useCallback(() => setModalTarjetaAbierta(false), []);
  useClickOutside(modalRef, cerrarModal);

  const empresa = empresaSuscripcion || suscripcion.empresa;
  const profesionalesVisibles = Math.max(1, profesionalesActivos);
  const profesionalesExtra = Math.max(0, profesionalesActivos - 1);

  useEffect(() => {
    if (suscripcion?._id) {
      dispatch(startLoadSuscripcionCompleta(suscripcion._id));
    }
  }, [dispatch, suscripcion?._id]);

  useEffect(() => {
    if (empresa?._id) {
      dispatch(startLoadProfesionalesActivos(empresa._id));
    }
  }, [dispatch, empresa?._id]);

  useEffect(() => {
    if (empresa?._id) {
      dispatch(startLoadTarjeta(empresa._id));
    }
  }, [dispatch, empresa?._id]);

  const handleFinishPayment = (result) => {
    setModalTarjetaAbierta(false);
    
    if (result === 'success') {
      dispatch(openModalBottom({ 
        text: ['¡Pago realizado con éxito! La suscripción ha sido procesada correctamente.'], 
        timeOut: 5 
      }));
      
      if (empresa?._id) {
        dispatch(startLoadTarjeta(empresa._id));
        dispatch(startLoadSuscripcionActiva(empresa._id));
      }
      
      if (suscripcion?._id) {
        dispatch(startLoadSuscripcionCompleta(suscripcion._id));
        dispatch(startLoadingTransacciones(suscripcion._id));
      }
    } else {
      if (empresa?._id) {
        dispatch(startLoadTarjeta(empresa._id));
        dispatch(startLoadSuscripcionActiva(empresa._id));
      }
    }
  };

  if (!empresa) return null;

  const PRECIO_BASE_PLAN = 34.90;
  const PRECIO_POR_PROFESIONAL = 8.00;

  const precioBase = PRECIO_BASE_PLAN;
  const precioPorProfesional = suscripcion?.precioPorProfesional || PRECIO_POR_PROFESIONAL;
  const subtotalEstimado = precioBase + profesionalesExtra * precioPorProfesional;
  const ivaEstimado = +(subtotalEstimado * 0.21).toFixed(2);
  const importeEstimado = +(subtotalEstimado + ivaEstimado).toFixed(2);

  const autoRenovar = suscripcion.autoRenovar !== undefined ? suscripcion.autoRenovar : true;
  const estaCancelada = autoRenovar === false;

  const onClickAbrirModalTarjeta = () => {
    if (suscripcion.estado === 'cancelada' || estaCancelada) {
      setAccionModal('activar');
    } else if (suscripcion.estado === 'trial') {
      if (tarjeta?.estado === 'activa') {
        setAccionModal('activar');
      } else {
        setAccionModal('añadir');
      }
    } else if (tarjeta?.estado === 'activa') {
      setAccionModal('cambiar');
    } else {
      setAccionModal('añadir');
    }
    setModalTarjetaAbierta(true);
  };

  const textoBoton =
    suscripcion.estado === 'cancelada' || estaCancelada
      ? 'Activar suscripción'
      : suscripcion.estado === 'trial'
        ? tarjeta?.estado === 'activa'
          ? 'Activar suscripción'
          : 'Añadir tarjeta'
        : tarjeta?.estado === 'activa'
          ? 'Cambiar tarjeta'
          : 'Añadir tarjeta';

  const getCardColor = (type) => {
    const colors = {
      'VISA': '#1A1F71',
      'MASTERCARD': '#FF6B35',
      'AMEX': '#006FCF',
      'DISCOVER': '#FF6000',
      'JCB': '#0B4EA2',
      'MAESTRO': '#0099DE',
      'DINERS CLUB': '#0079BE',
      'UNIONPAY': '#E21836'
    };
    
    return colors[type] || '#666666';
  };

  return (
    <section className="section datos">
      <h3 style={{ color: colores.colorPrincipal }}>Información de pago</h3>

      <div style={{ marginBottom: '30px' }}>
        <h5 className="datos-title" style={{ color: colores.color9 }}>PRÓXIMA FACTURA</h5>
        <div className="datos-row row-1">
          <div className="datos-col col-1">
            <div className="data-item" style={{ flex: '0 0 75%' }}>
              <label>Fecha</label>
              <div className="data-item-input">
                {isSuperAdmin ? (
                  <EditarFechaFinal suscripcion={suscripcion} onUpdate={onUpdate} />
                ) : (
                  suscripcion?.fechaRenovaciónPeriodo
                    ? new Date(suscripcion.fechaRenovaciónPeriodo).toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                      })
                    : 'N/D'
                )}
              </div>
            </div>
            <div className="data-item" style={{ flex: '0 0 25%' }}>
              <label>Profesionales actuales</label>
              <div className="data-item-input">{profesionalesVisibles}</div>
            </div>
          </div>
          <div className="datos-col col-2">
            <div className="data-item" style={{ flex: '1' }}>
              <label>Importe estimado</label>
              <div className="data-item-input">
                {importeEstimado}€/mes
                <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '7px' }}>
                  (Incluye 1 profesional. Subtotal: {subtotalEstimado.toFixed(2)}€, IVA incluido)
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: '30px' }}>
        <h5 className="datos-title" style={{ color: colores.color9 }}>FORMA DE PAGO</h5>
        <div className="datos-row row-2">
          <div className="datos-col col-1">
            <div style={{ flex: '1' }}>
              <div className='data-item' style={{ display: 'block' }}>
                <label style={{ marginBottom: '12px' }}>Tarjeta</label>
              </div>
              <div className="tarjeta-wrapper">
                {tarjeta?.tipo ? (
                  <div style={{
                    backgroundColor: getCardColor(tarjeta.tipo),
                    color: 'white',
                    padding: '4px 10px',
                    borderRadius: '5px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    display: 'inline-block',
                    border: 'none',
                    outline: 'none',
                    boxShadow: 'none',
                    textDecoration: 'none',
                    textAlign: 'center',
                    lineHeight: '1',
                    maxWidth: '110px',
                    width: 'fit-content'
                  }}>
                    {tarjeta.tipo}
                  </div>
                ) : (
                  <div className="tarjeta-tipo">{tarjeta?.tipo || 'Tipo de tarjeta'}</div>
                )}
                <div className="tarjeta-numero">{tarjeta?.numero || '**** **** **** ****'}</div>
                <div className="tarjeta-expiracion">Expira: {tarjeta?.expiracion || 'Fecha/Año'}</div>
                <div className="tarjeta-estado">{tarjeta?.estado || 'No registrada'}</div>
              </div>
            </div>
            <button
              className="datos-btn"
              style={{ 
                marginLeft: 'auto', 
                alignSelf: 'self-end', 
                paddingBottom: '12px'
              }}
              onClick={onClickAbrirModalTarjeta}
            >
              {textoBoton}
            </button>
          </div>
        </div>
      </div>

      <div className="datos-address" style={{ marginBottom: '30px' }}>
        <h5 className="datos-title" style={{ color: colores.color9 }}>DIRECCIÓN DE FACTURACIÓN</h5>
        <div className="datos-row row-1">
          <div className="datos-col col-1">
            <div className="data-item facturado">
              <label>Empresa</label>
              <div className="data-item-input">{empresa?.nombre || 'Sin nombre'}</div>
            </div>
            <div className="data-item facturado">
              <label>Email</label>
              <div className="data-item-input">{empresa?.email || 'Sin email'}</div>
            </div>
            <div className="data-item facturado">
              <label>CIF/NIF</label>
              <div className="data-item-input">{empresa?.cif || 'No disponible'}</div>
            </div>
          </div>
          <div className="datos-col col-2">
            <div className="data-item" style={{ flex: '0 0 75%' }}>
              <label>Dirección</label>
              <div className="data-item-input" style={{ lineHeight: '1.4', paddingRight: '20px' }}>
                {empresa?.direcciones?.[0]?.direccion || 'No disponible'}
              </div>
            </div>
            <div className="data-item" style={{ flex: '0 0 25%' }}>
              <label>País</label>
              <div className="data-item-input">{empresa?.pais || 'España'}</div>
            </div>
          </div>
        </div>
        <button
          className="datos-btn"
          onClick={() => history.push('/admin')}
        >
          Editar dirección
        </button>
      </div>

      <ModalPagarSuscripcion
        empresaId={empresa._id}
        suscripcion={suscripcion}
        isOpen={modalTarjetaAbierta}
        accion={accionModal}
        onClose={() => setModalTarjetaAbierta(false)}
        onFinishPayment={handleFinishPayment}
      />
    </section>
  );
};

export default InformacionPago;
